import React from 'react'

import Box from '@components/kit/box'
import { Row } from '@components/kit/grid'
import Text from '@components/kit/text'
import moment from 'moment-timezone'

import {
  IProgramOverview,
  IUserProgramStatus
} from '@store/HealthTracking/types'
import { colors } from '@theme'
import {
  ColumnWrapper,
  HorizontalRule,
  LabelText,
  RowWrapper,
  ValueText
} from '@components/table'

/* -----------------------------------------------------------------------------
 * StatusBadge
 * -------------------------------------------------------------------------- */

const STATUS_COLORS: Record<IUserProgramStatus, keyof typeof colors> = {
  APPROVED: 'lightGreen',
  PENDING: 'walrusBlue100',
  REJECTED: 'paleError'
}

const STATUS_TEXT_COLORS: Record<IUserProgramStatus, keyof typeof colors> = {
  APPROVED: 'walrusGreen500',
  PENDING: 'walrusBlue300',
  REJECTED: 'error'
}

const StatusBadge = ({ status }: { status: IUserProgramStatus }) => {
  const label = status.charAt(0).toUpperCase() + status.slice(1)
  return (
    <Box
      backgroundColor={STATUS_COLORS[status]}
      borderRadius='medium'
      px='08'
      py='04'
      alignSelf='flex-start'
    >
      <Text font='text.12.bold' textColor={STATUS_TEXT_COLORS[status]}>
        {label}
      </Text>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * CurrentOverview
 * -------------------------------------------------------------------------- */

interface CurrentOverviewProps {
  overview: IProgramOverview | null
}

function CurrentOverview({ overview }: CurrentOverviewProps) {
  const programs = overview?.programs ?? []
  const bmi = overview?.bmi != null ? overview.bmi.toFixed(1) : '-'
  const weight = overview?.weight != null ? `${overview.weight} lbs` : '-'
  const weightDate = overview?.weight_date
    ? moment(overview.weight_date).format('MM/DD/YYYY')
    : '-'
  const programStatus = programs.length > 0 ? programs[0].status : null

  return (
    <>
      <RowWrapper>
        <LabelText font='display.14.bold'>Current Overview</LabelText>
      </RowWrapper>

      <Row width='100%'>
        <ColumnWrapper width='50%'>
          <LabelText>Date</LabelText>
          <ValueText>{weightDate}</ValueText>
        </ColumnWrapper>
        <ColumnWrapper width='50%'>
          <LabelText>BMI</LabelText>
          <ValueText>{bmi}</ValueText>
        </ColumnWrapper>
      </Row>

      <Row width='100%'>
        <ColumnWrapper width='50%'>
          <LabelText>Weight</LabelText>
          <ValueText>{weight}</ValueText>
        </ColumnWrapper>
        <ColumnWrapper width='50%'>
          <LabelText>Program Status</LabelText>
          {programStatus ? (
            <StatusBadge status={programStatus} />
          ) : (
            <ValueText>-</ValueText>
          )}
        </ColumnWrapper>
      </Row>

      <HorizontalRule />
    </>
  )
}

export default CurrentOverview
